import { ConnectError, Code } from "@connectrpc/connect";

import { authClient } from "@/services/api";
import { useAppStore } from "@/stores/app";

import type { User } from "@/gen/tempus/v1/tempus_pb";

// useAuth wraps the AuthService RPCs and keeps the app store's currentUser in
// sync. The session itself lives in an httpOnly cookie managed by the server;
// nothing here touches tokens directly.
export function useAuth() {
  const appStore = useAppStore();

  async function login(email: string, password: string): Promise<User> {
    const user = await authClient.login({ email, password });
    appStore.setCurrentUser(user);
    return user;
  }

  async function signup(params: {
    email: string;
    password: string;
    name: string;
    inviteCode: string;
  }): Promise<User> {
    const user = await authClient.signup(params);
    appStore.setCurrentUser(user);
    return user;
  }

  async function logout(): Promise<void> {
    try {
      await authClient.logout({});
    } finally {
      appStore.clearCurrentUser();
    }
  }

  // Verify any existing session cookie via Me(). Returns whether a valid
  // session exists and marks the store's auth state as checked either way.
  async function refreshSession(): Promise<boolean> {
    try {
      const user = await authClient.me({});
      appStore.setCurrentUser(user);
      return true;
    } catch (error) {
      appStore.setCurrentUser(null);
      // Unauthenticated is the normal "logged out" case; log anything else.
      if (!(error instanceof ConnectError) || error.code !== Code.Unauthenticated) {
        console.error("Failed to verify session:", error);
      }
      return false;
    }
  }

  return { login, signup, logout, refreshSession };
}
